// GenesysSendModal.tsx

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import type { GenesysSendInfo } from "../../utils";

export type GenesysSendStatus =
  | "idle"
  | "pending"
  | "running"
  | "success"
  | "interrupted"
  | "error";

interface GenesysSendModalProps {
  open: boolean;
  campaignLabel: string;
  status: GenesysSendStatus;
  progress: number;
  elapsedSeconds: number;
  etaSeconds?: number;
  info?: GenesysSendInfo;
  cancelRequested?: boolean;
  isCancelling?: boolean;
  notice?: string;
  errorDetalle?: unknown[];
  isLoadingErrores?: boolean;
  errorMessage?: string;
  onClose: () => void;
  onCancel?: () => void;
  onViewErrores?: () => void;
}

const formatEta = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
};

const getTitle = (status: GenesysSendStatus): string => {
  switch (status) {
    case "pending":
      return "Iniciando envío a Genesys";
    case "running":
      return "Enviando lista de contactos a Genesys";
    case "success":
      return "Envío completado";
    case "interrupted":
      return "Envío detenido";
    default:
      return "Error en el envío";
  }
};

const hasBlockErrors = (info?: GenesysSendInfo): boolean =>
  !!info &&
  ((typeof info.contactosError === "number" && info.contactosError > 0) ||
    (typeof info.bloquesError === "number" && info.bloquesError > 0));

const GenesysSendModal = ({
  open,
  campaignLabel,
  status,
  progress,
  elapsedSeconds,
  info,
  cancelRequested,
  isCancelling,
  etaSeconds,
  notice,
  errorDetalle,
  isLoadingErrores,
  errorMessage,
  onClose,
  onCancel,
  onViewErrores,
}: GenesysSendModalProps) => {
  const isBusy = status === "pending" || status === "running";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="genesys-send-modal-title"
    >
      <DialogTitle
        id="genesys-send-modal-title"
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <SendIcon
            color={
              isBusy
                ? "secondary"
                : status === "success"
                  ? "success"
                  : status === "interrupted"
                    ? "warning"
                    : "error"
            }
          />
          <Typography variant="h6" component="span">
            {getTitle(status)}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" aria-label="Cerrar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Campaña: <strong>{campaignLabel}</strong>
          </Typography>

          {status === "pending" && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} color="secondary" />
                <Typography variant="body2">
                  Iniciando proceso de envío...
                </Typography>
              </Stack>
            </Stack>
          )}

          {status === "running" && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} color="secondary" />
                <Typography variant="body2">
                  {info?.fase || "Procesando..."}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress}
                color="secondary"
                sx={{ height: 8, borderRadius: 4 }}
              />
              {(typeof info?.bloqueActual === "number" ||
                typeof info?.enviados === "number") && (
                <Typography variant="body2">
                  {typeof info?.bloqueActual === "number" &&
                  typeof info?.bloquesTotal === "number"
                    ? `Enviando ${info.bloqueActual}/${info.bloquesTotal} bloques${
                        typeof info.bloquesOk === "number" && info.bloquesTotal > 0
                          ? ` (${Math.round((info.bloquesOk / info.bloquesTotal) * 100)}%)`
                          : ""
                      }`
                    : "Procesando envío"}
                  {typeof info?.enviados === "number"
                    ? ` · ${info.enviados.toLocaleString()} enviados`
                    : ""}
                  {typeof info?.faltantes === "number"
                    ? ` · faltan ${info.faltantes.toLocaleString()}`
                    : ""}
                </Typography>
              )}
              <Grid container spacing={1}>
                <SummaryStat
                  label="Bloque"
                  value={info?.bloqueActual}
                  suffix={
                    typeof info?.bloquesTotal === "number"
                      ? ` de ${info.bloquesTotal}`
                      : ""
                  }
                />
                <SummaryStat
                  label="Enviados a Genesys"
                  value={info?.enviados}
                  suffix={
                    typeof info?.total === "number"
                      ? ` de ${info.total}`
                      : ""
                  }
                />
                <SummaryStat
                  label="Faltan por enviar"
                  value={info?.faltantes}
                  suffix={
                    typeof info?.pendientes === "number"
                      ? ` de ${info.pendientes}`
                      : ""
                  }
                />
                <SummaryStat label="Bloques OK" value={info?.bloquesOk} />
                <SummaryStat
                  label="Bloques con error"
                  value={info?.bloquesError}
                  error={typeof info?.bloquesError === "number" && info.bloquesError > 0}
                />
                <SummaryStat
                  label="Contactos con error"
                  value={info?.contactosError}
                  error={typeof info?.contactosError === "number" && info.contactosError > 0}
                />
              </Grid>
              {notice && <Alert severity="info">{notice}</Alert>}
              {cancelRequested && (
                <Alert severity="warning">
                  Cancelación solicitada, esperando confirmación del servidor...
                </Alert>
              )}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="caption" color="text.secondary">
                  Tiempo transcurrido: {elapsedSeconds} s
                  {typeof etaSeconds === "number"
                    ? ` · Restante estimado: ${formatEta(etaSeconds)}`
                    : ""}
                </Typography>
                <Typography variant="caption" fontWeight={700} color="secondary.main">
                  {Math.round(progress)}%
                </Typography>
              </Stack>
            </Stack>
          )}

          {status === "success" && info && (
            <Stack spacing={2}>
              <Stack spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
                <Typography variant="body1" fontWeight={600} textAlign="center">
                  La campaña {campaignLabel} fue procesada correctamente.
                </Typography>
              </Stack>
              <BigNumber
                value={info.enviados}
                label="contactos enviados a Genesys en esta ejecución"
              />
              <SummaryGrid info={info} />
              <ErrorNotice
                info={info}
                errorDetalle={errorDetalle}
                isLoadingErrores={isLoadingErrores}
                onViewErrores={onViewErrores}
              />
              {!errorDetalle && info.errores.length > 0 && (
                <ErroresBox errores={info.errores} />
              )}
            </Stack>
          )}

          {status === "interrupted" && info && (
            <Stack spacing={2}>
              <Stack spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                <WarningAmberIcon color="warning" sx={{ fontSize: 56 }} />
                <Typography variant="body1" fontWeight={600} textAlign="center">
                  La campaña {campaignLabel} fue detenida.
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  El envío se interrumpió entre bloques. Lo ya enviado quedó
                  guardado.
                </Typography>
              </Stack>
              <BigNumber
                value={info.enviados}
                label="contactos enviados antes de la detención"
                color="warning.main"
              />
              {typeof info.faltantes === "number" && info.faltantes > 0 && (
                <Typography variant="body2" textAlign="center">
                  Faltan <strong>{info.faltantes.toLocaleString()}</strong> por
                  enviar; se retomarán automáticamente al re-ejecutar la
                  campaña.
                </Typography>
              )}
              <SummaryGrid info={info} />
              <ErrorNotice
                info={info}
                errorDetalle={errorDetalle}
                isLoadingErrores={isLoadingErrores}
                onViewErrores={onViewErrores}
              />
              {!errorDetalle && info.errores.length > 0 && (
                <ErroresBox errores={info.errores} />
              )}
            </Stack>
          )}

          {status === "error" && (
            <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
              <Stack spacing={1} alignItems="center">
                <ErrorIcon color="error" sx={{ fontSize: 56 }} />
                <Typography variant="body2" color="error.main" textAlign="center">
                  {errorMessage
                    ? `Error en la campaña ${campaignLabel}: ${errorMessage}`
                    : `No se pudo completar el envío de la lista de contactos de la campaña ${campaignLabel}.`}
                </Typography>
              </Stack>
              {info && info.errores.length > 0 && <ErroresBox errores={info.errores} />}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      {status === "running" && (
        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 1.5 }}>
          <Button
            color="error"
            variant="outlined"
            startIcon={<StopCircleIcon />}
            onClick={onCancel}
            disabled={isCancelling || cancelRequested}
          >
            Cancelar envío
          </Button>
          <Button onClick={onClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      )}

      {!isBusy && (
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      )}

      {isBusy && (
        <Box sx={{ px: 3, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Puede cerrar la ventana; el proceso continuará en segundo plano.
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

interface BigNumberProps {
  value?: number;
  label: string;
  color?: string;
}

const BigNumber = ({ value, label, color }: BigNumberProps) => {
  if (typeof value !== "number") return null;

  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography variant="h4" fontWeight={700} color={color || "primary.main"}>
        {value.toLocaleString()}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
};

const SummaryGrid = ({ info }: { info: GenesysSendInfo }) => {
  return (
    <Grid container spacing={1}>
      <SummaryStat label="Total (Softnet)" value={info.total} />
      <SummaryStat label="Enviados en esta ejecución" value={info.enviados} />
      <SummaryStat label="Bloques OK" value={info.bloquesOk} />
      <SummaryStat
        label="Bloques con error"
        value={info.bloquesError}
        error={typeof info.bloquesError === "number" && info.bloquesError > 0}
      />
      <SummaryStat
        label="Contactos con error"
        value={info.contactosError}
        error={typeof info.contactosError === "number" && info.contactosError > 0}
      />
    </Grid>
  );
};

const ErroresBox = ({ errores }: { errores: unknown[] }) => {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: "error.light",
        maxHeight: 120,
        overflow: "auto",
      }}
    >
      <Typography variant="caption" color="error.main" sx={{ fontWeight: 700 }}>
        Errores ({errores.length}):
      </Typography>
      {errores.slice(0, 5).map((errorItem, index) => (
        <Typography key={index} variant="caption" color="error.main" display="block">
          • {typeof errorItem === "string" ? errorItem : JSON.stringify(errorItem)}
        </Typography>
      ))}
    </Box>
  );
};

interface ErrorNoticeProps {
  info: GenesysSendInfo;
  errorDetalle?: unknown[];
  isLoadingErrores?: boolean;
  onViewErrores?: () => void;
}

const ErrorNotice = ({
  info,
  errorDetalle,
  isLoadingErrores,
  onViewErrores,
}: ErrorNoticeProps) => {
  if (!hasBlockErrors(info)) return null;

  return (
    <Stack spacing={1}>
      <Alert severity="warning">
        {typeof info.contactosError === "number" && info.contactosError > 0
          ? `${info.contactosError.toLocaleString()} contactos no se enviaron (persistidos en BD).`
          : `${info.bloquesError ?? 0} bloques terminaron con error.`}
      </Alert>
      {!errorDetalle && onViewErrores && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={onViewErrores}
          disabled={isLoadingErrores}
          sx={{ alignSelf: "flex-start" }}
        >
          {isLoadingErrores ? "Consultando..." : "Ver errores"}
        </Button>
      )}
      {errorDetalle && errorDetalle.length > 0 && (
        <ErroresBox errores={errorDetalle} />
      )}
    </Stack>
  );
};

interface SummaryStatProps {
  label: string;
  value?: number;
  suffix?: string;
  highlight?: boolean;
  error?: boolean;
}

const SummaryStat = ({ label, value, suffix, highlight, error }: SummaryStatProps) => {
  return (
    <Grid item xs={6} sm={4}>
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: "action.hover",
          border: 1,
          borderColor: error ? "error.main" : "divider",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          color={error ? "error.main" : highlight ? "primary.main" : "text.primary"}
        >
          {typeof value === "number" ? value.toLocaleString() : "—"}
          {suffix && (
            <Typography component="span" variant="caption" color="text.secondary">
              {suffix}
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Grid>
  );
};

export default GenesysSendModal;
